
import { parse } from "../fable_modules/fable-library-js.5.13.0/Int32.js";
import { filter, tryFind as tryFind_1, sortBy, ofArray, isEmpty, sum, fold as fold_1, map, append, empty, collect, singleton, item as item_1, findIndexBack } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { stringHash, int32ToString, comparePrimitives, compare } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { PrimaryT_$reflection, NumberT_$reflection, UnaryT_$reflection, ExpressionT_$reflection, ExtraErrorInfo, ReplaceType, OneUnary, ErrorInfo } from "./VerilogTypes.js";
import { find, tryFind, ofList } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { Number$, getPrimaryLocation, BaseDU, getPrimaryName, getItem, evalExpr, evalExprWithParams } from "./VerilogAST.js";
import { map as map_1, item as item_2, fold } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { length as length_1, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";
import { Union, toString } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { toText, printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { toDecimal, strToIntCheckWidth } from "../Simulator/NumberHelpers.js";
import { toString as toString_1 } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { defaultArg, value as value_2 } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { union_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { contains, ofList as ofList_1, union, empty as empty_1 } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { except } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";

/**
 * Helper function to evaluate numeric bit-select bounds
 */
export function evalNumber(num) {
    return parse(num, 511, false, 32) | 0;
}

/**
 * Helper function to create an ErrorInfo-type Error Message
 * given the location, the variable name, and the message
 */
export function createErrorMessage(newLinesLocations, currLocation, message, extraMessages, name) {
    const prevIndex = findIndexBack((x_1) => (compare(x_1, currLocation) <= 0), newLinesLocations) | 0;
    return singleton(new ErrorInfo(prevIndex + 1, (currLocation - item_1(prevIndex, newLinesLocations)) + 1, name.length, message, extraMessages));
}

/**
 * Returns the names of the declared WIRES
 */
export function getWireSizeMap(items, paramMap) {
    return ofList(collect((x) => {
        if (x.tag === 3) {
            const cont = x.fields[0];
            if (cont.StatementType.tag === 1) {
                const matchValue_1 = cont.Assignment.LHS.PrimaryType;
                switch (matchValue_1.tag) {
                    case 1:
                        return singleton([matchValue_1.fields[0].Name, 1]);
                    case 3:
                        return singleton([matchValue_1.fields[0].Name, evalExprWithParams(matchValue_1.fields[1], paramMap)]);
                    case 2:
                        return singleton([matchValue_1.fields[0].Name, (matchValue_1.fields[1] - matchValue_1.fields[2]) + 1]);
                    case 4:
                        return singleton([matchValue_1.fields[0].Name, matchValue_1.fields[2]]);
                    case 5:
                        return empty();
                    case 6:
                        return empty();
                    default:
                        return singleton([matchValue_1.fields[0].Name, 1]);
                }
            }
            else {
                return empty();
            }
        }
        else {
            return empty();
        }
    }, items), {
        Compare: (x_1, y) => (comparePrimitives(x_1, y) | 0),
    });
}

export function getPrimaryWidth(portSizeMap, primary) {
    let matchResult, expr, id_1;
    switch (primary.tag) {
        case 1: {
            matchResult = 1;
            break;
        }
        case 5: {
            matchResult = 2;
            break;
        }
        case 2: {
            matchResult = 3;
            break;
        }
        case 4: {
            matchResult = 4;
            break;
        }
        case 6: {
            matchResult = 5;
            expr = primary.fields[2];
            id_1 = primary.fields[0];
            break;
        }
        case 3: {
            matchResult = 5;
            expr = primary.fields[1];
            id_1 = primary.fields[0];
            break;
        }
        default:
            matchResult = 0;
    }
    switch (matchResult) {
        case 0: {
            const matchValue = tryFind(primary.fields[0].Name, portSizeMap);
            if (matchValue != null) {
                return matchValue | 0;
            }
            else {
                return 1;
            }
        }
        case 1:
            return 1;
        case 2:
            return ((primary.fields[2] - primary.fields[3]) + 1) | 0;
        case 3:
            return ((primary.fields[1] - primary.fields[2]) + 1) | 0;
        case 4:
            return primary.fields[2] | 0;
        default: {
            const matchValue_1 = tryFind(id_1.Name, portSizeMap);
            if (matchValue_1 != null) {
                return matchValue_1 | 0;
            }
            else {
                return 1;
            }
        }
    }
}

/**
 * return line number based on location
 */
export function getLineNumber(linesLocations, location) {
    return (findIndexBack((x) => (x <= location), linesLocations) + 1) | 0;
}

/**
 * Recursive function to get all the primaries used in the RHS of an assignment
 * Used by checkNamesOnRHSOfAssignment and checkSizesOnRHSOfAssignment
 */
export function primariesUsedInAssignment(inLst_mut, tree_mut) {
    primariesUsedInAssignment:
    while (true) {
        const inLst = inLst_mut, tree = tree_mut;
        let matchResult, a, b;
        switch (tree.tag) {
            case 12: {
                matchResult = 1;
                break;
            }
            case 11: {
                matchResult = 2;
                break;
            }
            case 14: {
                matchResult = 3;
                break;
            }
            case 0: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 1: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 2: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 3: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 4: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 5: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 6: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 7: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 8: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 9: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 10: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 15: {
                matchResult = 5;
                break;
            }
            default:
                matchResult = 0;
        }
        switch (matchResult) {
            case 0: {
                const u = tree.fields[0];
                switch (u.tag) {
                    case 2: {
                        inLst_mut = inLst;
                        tree_mut = u.fields[0];
                        continue primariesUsedInAssignment;
                    }
                    case 3:
                        return fold(primariesUsedInAssignment, inLst, u.fields[0]);
                    case 1:
                        return inLst;
                    case 4:
                        return append(inLst, singleton(u.fields[0]));
                    default:
                        return append(inLst, singleton(u.fields[0]));
                }
            }
            case 1: {
                const u_1 = tree.fields[0];
                switch (u_1.tag) {
                    case 2: {
                        inLst_mut = inLst;
                        tree_mut = u_1.fields[0];
                        continue primariesUsedInAssignment;
                    }
                    case 3:
                        return fold(primariesUsedInAssignment, inLst, u_1.fields[0]);
                    case 1:
                        return inLst;
                    case 4:
                        return append(inLst, singleton(u_1.fields[0]));
                    default:
                        return append(inLst, singleton(u_1.fields[0]));
                }
            }
            case 2: {
                inLst_mut = inLst;
                tree_mut = tree.fields[1];
                continue primariesUsedInAssignment;
            }
            case 3:
                return inLst;
            case 4: {
                inLst_mut = primariesUsedInAssignment(inLst, a);
                tree_mut = b;
                continue primariesUsedInAssignment;
            }
            default: {
                inLst_mut = primariesUsedInAssignment(primariesUsedInAssignment(inLst, tree.fields[0]), tree.fields[1]);
                tree_mut = tree.fields[2];
                continue primariesUsedInAssignment;
            }
        }
        break;
    }
}

export function numbersUsedInAssignment(inLst_mut, tree_mut) {
    numbersUsedInAssignment:
    while (true) {
        const inLst = inLst_mut, tree = tree_mut;
        let matchResult, a, b;
        switch (tree.tag) {
            case 12: {
                matchResult = 1;
                break;
            }
            case 11: {
                matchResult = 2;
                break;
            }
            case 14: {
                matchResult = 3;
                break;
            }
            case 0: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 1: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 2: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 3: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 4: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 5: {
                matchResult = 4;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 6: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 7: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 8: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 9: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 10: {
                matchResult = 4;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 15: {
                matchResult = 5;
                break;
            }
            default:
                matchResult = 0;
        }
        switch (matchResult) {
            case 0: {
                const u = tree.fields[0];
                switch (u.tag) {
                    case 2: {
                        inLst_mut = inLst;
                        tree_mut = u.fields[0];
                        continue numbersUsedInAssignment;
                    }
                    case 3:
                        return fold(numbersUsedInAssignment, inLst, u.fields[0]);
                    case 0:
                        return inLst;
                    case 4:
                        return inLst;
                    default:
                        return append(inLst, singleton(u.fields[0]));
                }
            }
            case 1: {
                const u_1 = tree.fields[0];
                switch (u_1.tag) {
                    case 2: {
                        inLst_mut = inLst;
                        tree_mut = u_1.fields[0];
                        continue numbersUsedInAssignment;
                    }
                    case 3:
                        return fold(numbersUsedInAssignment, inLst, u_1.fields[0]);
                    case 0:
                        return inLst;
                    case 4:
                        return inLst;
                    default:
                        return append(inLst, singleton(u_1.fields[0]));
                }
            }
            case 2: {
                inLst_mut = inLst;
                tree_mut = tree.fields[1];
                continue numbersUsedInAssignment;
            }
            case 3:
                return append(inLst, singleton(tree.fields[0]));
            case 4: {
                inLst_mut = numbersUsedInAssignment(inLst, a);
                tree_mut = b;
                continue numbersUsedInAssignment;
            }
            default: {
                inLst_mut = numbersUsedInAssignment(numbersUsedInAssignment(inLst, tree.fields[0]), tree.fields[1]);
                tree_mut = tree.fields[2];
                continue numbersUsedInAssignment;
            }
        }
        break;
    }
}

/**
 * replace this later with getLHSBits'!
 */
export function getLHSBits(portSizeMap, assignment) {
    let assignmentWithRange;
    const matchValue = assignment.LHS.PrimaryType;
    switch (matchValue.tag) {
        case 5: {
            assignmentWithRange = [matchValue.fields[0].Name, matchValue.fields[2], matchValue.fields[3]];
            break;
        }
        case 1: {
            const idx = matchValue.fields[1] | 0;
            assignmentWithRange = [matchValue.fields[0].Name, idx, idx];
            break;
        }
        case 3: {
            assignmentWithRange = [matchValue.fields[0].Name, -1, -1];
            break;
        }
        case 6: {
            assignmentWithRange = [matchValue.fields[0].Name, -1, -1];
            break;
        }
        case 2: {
            assignmentWithRange = [matchValue.fields[0].Name, matchValue.fields[1], matchValue.fields[2]];
            break;
        }
        case 4: {
            const width = matchValue.fields[2] | 0;
            const bStart = evalExpr(matchValue.fields[1]) | 0;
            assignmentWithRange = [matchValue.fields[0].Name, bStart, (matchValue.fields[3].tag === 1) ? ((bStart - width) + 1) : ((bStart + width) - 1)];
            break;
        }
        default:
            assignmentWithRange = [matchValue.fields[0].Name, -1, -1];
    }
    let matchResult, name_3, x_2, y_3, bEnd_1, bStart_1, name_4;
    if (assignmentWithRange[1] === -1) {
        if (assignmentWithRange[2] === -1) {
            matchResult = 0;
        }
        else if (assignmentWithRange[1] === assignmentWithRange[2]) {
            matchResult = 1;
            name_3 = assignmentWithRange[0];
            x_2 = assignmentWithRange[1];
            y_3 = assignmentWithRange[2];
        }
        else {
            matchResult = 2;
            bEnd_1 = assignmentWithRange[2];
            bStart_1 = assignmentWithRange[1];
            name_4 = assignmentWithRange[0];
        }
    }
    else if (assignmentWithRange[1] === assignmentWithRange[2]) {
        matchResult = 1;
        name_3 = assignmentWithRange[0];
        x_2 = assignmentWithRange[1];
        y_3 = assignmentWithRange[2];
    }
    else {
        matchResult = 2;
        bEnd_1 = assignmentWithRange[2];
        bStart_1 = assignmentWithRange[1];
        name_4 = assignmentWithRange[0];
    }
    switch (matchResult) {
        case 0: {
            const name_2 = assignmentWithRange[0];
            const matchValue_1 = tryFind(name_2, portSizeMap);
            if (matchValue_1 == null) {
                return empty();
            }
            else {
                return map((y_2) => [name_2 + int32ToString(y_2), name_2], toList(rangeDouble(0, 1, matchValue_1 - 1)));
            }
        }
        case 1:
            return singleton([name_3 + int32ToString(x_2), name_3]);
        default:
            return map((y_4) => [name_4 + int32ToString(y_4), name_4], toList(rangeDouble(bEnd_1, 1, bStart_1)));
    }
}

/**
 * returns all the bits of the lhs of an assignment
 * the strings returned are unique, index surrounded by "[]" is appended to the name of the variable
 */
export function getLHSBits$0027(portSizeMap, assignment) {
    let assignmentWithRange;
    const matchValue = assignment.LHS.PrimaryType;
    switch (matchValue.tag) {
        case 5: {
            assignmentWithRange = [matchValue.fields[0].Name, -1, -1];
            break;
        }
        case 6: {
            assignmentWithRange = [matchValue.fields[0].Name, -1, -1];
            break;
        }
        case 1: {
            const idx = matchValue.fields[1] | 0;
            assignmentWithRange = [matchValue.fields[0].Name, idx, idx];
            break;
        }
        case 3: {
            assignmentWithRange = [matchValue.fields[0].Name, -1, -1];
            break;
        }
        case 2: {
            assignmentWithRange = [matchValue.fields[0].Name, matchValue.fields[1], matchValue.fields[2]];
            break;
        }
        case 4: {
            const width = matchValue.fields[2] | 0;
            const bStart = evalExpr(matchValue.fields[1]) | 0;
            assignmentWithRange = [matchValue.fields[0].Name, bStart, (matchValue.fields[3].tag === 1) ? ((bStart - width) + 1) : ((bStart + width) - 1)];
            break;
        }
        default:
            assignmentWithRange = [matchValue.fields[0].Name, -1, -1];
    }
    let matchResult, name_3, x_2, y_3, bEnd_1, bStart_1, name_4;
    if (assignmentWithRange[1] === -1) {
        if (assignmentWithRange[2] === -1) {
            matchResult = 0;
        }
        else if (assignmentWithRange[1] === assignmentWithRange[2]) {
            matchResult = 1;
            name_3 = assignmentWithRange[0];
            x_2 = assignmentWithRange[1];
            y_3 = assignmentWithRange[2];
        }
        else {
            matchResult = 2;
            bEnd_1 = assignmentWithRange[2];
            bStart_1 = assignmentWithRange[1];
            name_4 = assignmentWithRange[0];
        }
    }
    else if (assignmentWithRange[1] === assignmentWithRange[2]) {
        matchResult = 1;
        name_3 = assignmentWithRange[0];
        x_2 = assignmentWithRange[1];
        y_3 = assignmentWithRange[2];
    }
    else {
        matchResult = 2;
        bEnd_1 = assignmentWithRange[2];
        bStart_1 = assignmentWithRange[1];
        name_4 = assignmentWithRange[0];
    }
    switch (matchResult) {
        case 0: {
            const name_2 = assignmentWithRange[0];
            const matchValue_1 = tryFind(name_2, portSizeMap);
            if (matchValue_1 == null) {
                return empty();
            }
            else {
                return map((y_2) => (((name_2 + "[") + int32ToString(y_2)) + "]"), toList(rangeDouble(0, 1, matchValue_1 - 1)));
            }
        }
        case 1:
            return singleton(((name_3 + "[") + int32ToString(x_2)) + "]");
        default:
            return map((y_4) => (((name_4 + "[") + int32ToString(y_4)) + "]"), toList(rangeDouble(bEnd_1, 1, bStart_1)));
    }
}

/**
 * returns each bit of an assignment LHS. In the case of variable indexing, no bits are returned
 */
export function getLHSBitsAssignedCertainly(portSizeMap, paramMap, assignment) {
    if (assignment.LHS.VariableBitSelect != null) {
        return empty();
    }
    else {
        const matchValue_1 = assignment.LHS.PrimaryType;
        switch (matchValue_1.tag) {
            case 5:
                return map((y_1) => (((((matchValue_1.fields[0].Name + "[") + toString(item_2(0, matchValue_1.fields[1]))) + "][") + int32ToString(y_1)) + "]"), toList(rangeDouble(0, 1, (matchValue_1.fields[3] - matchValue_1.fields[2]) - 1)));
            case 3:
                return empty();
            case 2:
                return map((y_2) => (((matchValue_1.fields[0].Name + "[") + int32ToString(y_2)) + "]"), toList(rangeDouble(matchValue_1.fields[2], 1, matchValue_1.fields[1])));
            case 1:
                return singleton(((matchValue_1.fields[0].Name + "[") + int32ToString(matchValue_1.fields[1])) + "]");
            case 4:
                return empty();
            case 6:
                return singleton(((matchValue_1.fields[0].Name + "[") + toString(item_2(0, matchValue_1.fields[1]))) + "]");
            default: {
                const id = matchValue_1.fields[0];
                const matchValue_2 = tryFind(id.Name, portSizeMap);
                if (matchValue_2 == null) {
                    return empty();
                }
                else {
                    return map((y) => (((id.Name + "[") + int32ToString(y)) + "]"), toList(rangeDouble(0, 1, matchValue_2 - 1)));
                }
            }
        }
    }
}

export function getPrimaryBits(portSizeMap, primary) {
    let primaryWithRange;
    switch (primary.tag) {
        case 5: {
            primaryWithRange = [primary.fields[0].Name, primary.fields[2], primary.fields[3]];
            break;
        }
        case 6: {
            primaryWithRange = [primary.fields[0].Name, -1, -1];
            break;
        }
        case 3: {
            primaryWithRange = [primary.fields[0].Name, -1, -1];
            break;
        }
        case 1: {
            const idx_1 = primary.fields[1] | 0;
            primaryWithRange = [primary.fields[0].Name, idx_1, idx_1];
            break;
        }
        case 2: {
            primaryWithRange = [primary.fields[0].Name, primary.fields[1], primary.fields[2]];
            break;
        }
        case 4: {
            const width = primary.fields[2] | 0;
            const bStart = evalExpr(primary.fields[1]) | 0;
            primaryWithRange = [primary.fields[0].Name, bStart, (primary.fields[3].tag === 1) ? ((bStart - width) + 1) : ((bStart + width) - 1)];
            break;
        }
        default:
            primaryWithRange = [primary.fields[0].Name, -1, -1];
    }
    let matchResult, name_3, x_2, y_3, bEnd_1, bStart_1, name_4;
    if (primaryWithRange[1] === -1) {
        if (primaryWithRange[2] === -1) {
            matchResult = 0;
        }
        else if (primaryWithRange[1] === primaryWithRange[2]) {
            matchResult = 1;
            name_3 = primaryWithRange[0];
            x_2 = primaryWithRange[1];
            y_3 = primaryWithRange[2];
        }
        else {
            matchResult = 2;
            bEnd_1 = primaryWithRange[2];
            bStart_1 = primaryWithRange[1];
            name_4 = primaryWithRange[0];
        }
    }
    else if (primaryWithRange[1] === primaryWithRange[2]) {
        matchResult = 1;
        name_3 = primaryWithRange[0];
        x_2 = primaryWithRange[1];
        y_3 = primaryWithRange[2];
    }
    else {
        matchResult = 2;
        bEnd_1 = primaryWithRange[2];
        bStart_1 = primaryWithRange[1];
        name_4 = primaryWithRange[0];
    }
    switch (matchResult) {
        case 0: {
            const name_2 = primaryWithRange[0];
            const matchValue = tryFind(name_2, portSizeMap);
            if (matchValue == null) {
                return empty();
            }
            else {
                return map((y_2) => (((name_2 + "[") + int32ToString(y_2)) + "]"), toList(rangeDouble(0, 1, matchValue - 1)));
            }
        }
        case 1:
            return singleton(((name_3 + "[") + int32ToString(x_2)) + "]");
        default:
            return map((y_4) => (((name_4 + "[") + int32ToString(y_4)) + "]"), toList(rangeDouble(bEnd_1, 1, bStart_1)));
    }
}

/**
 * Helper functions to extract the name and location of the LHS of an assignment
 */
export function lhsName(lhs) {
    const matchValue = lhs.PrimaryType;
    let id;
    switch (matchValue.tag) {
        case 1: {
            id = matchValue.fields[0];
            break;
        }
        case 2: {
            id = matchValue.fields[0];
            break;
        }
        case 3: {
            id = matchValue.fields[0];
            break;
        }
        case 4: {
            id = matchValue.fields[0];
            break;
        }
        case 5: {
            id = matchValue.fields[0];
            break;
        }
        case 6: {
            id = matchValue.fields[0];
            break;
        }
        default:
            id = matchValue.fields[0];
    }
    return id.Name;
}

export function lhsLocation(lhs) {
    const matchValue = lhs.PrimaryType;
    let id;
    switch (matchValue.tag) {
        case 1: {
            id = matchValue.fields[0];
            break;
        }
        case 2: {
            id = matchValue.fields[0];
            break;
        }
        case 3: {
            id = matchValue.fields[0];
            break;
        }
        case 4: {
            id = matchValue.fields[0];
            break;
        }
        case 5: {
            id = matchValue.fields[0];
            break;
        }
        case 6: {
            id = matchValue.fields[0];
            break;
        }
        default:
            id = matchValue.fields[0];
    }
    return id.Location | 0;
}

export function getDeclarations(declarations, node) {
    if (node.tag === 3) {
        return append(declarations, singleton(node.fields[0]));
    }
    else {
        return declarations;
    }
}

export function getCaseStatements(caseStatements, node) {
    if (node.tag === 6) {
        return append(caseStatements, singleton(node.fields[0]));
    }
    else {
        return caseStatements;
    }
}

export function getCaseStatementsWithLoc(caseStatements, node) {
    if (node.tag === 5) {
        const statement = node.fields[0];
        if (statement.tag === 3) {
            const case$ = statement.fields[0];
            return append(caseStatements, singleton([case$, case$.Location]));
        }
        else {
            return caseStatements;
        }
    }
    else {
        return caseStatements;
    }
}

export function getAlwaysBlocksWithLocations(alwaysBlocks, node) {
    if (node.tag === 18) {
        const matchValue = getItem(node.fields[0]);
        if (matchValue.tag === 4) {
            const always = matchValue.fields[0];
            return append(alwaysBlocks, singleton([always, always.Location]));
        }
        else {
            return alwaysBlocks;
        }
    }
    else {
        return alwaysBlocks;
    }
}

export function getCaseItemNums(nums, node) {
    if (node.tag === 17) {
        return append(nums, singleton(node.fields[0]));
    }
    else {
        return nums;
    }
}

export function getForStatementsWithLoc(forStatements, node) {
    if (node.tag === 5) {
        const statement = node.fields[0];
        if (statement.tag === 5) {
            const f = statement.fields[0];
            return append(forStatements, singleton([f, f.Location]));
        }
        else {
            return forStatements;
        }
    }
    else {
        return forStatements;
    }
}

/**
 * Helper function used by checkWidthOfAssignment
 * with 3 recursive subfunctions
 * Returns the RHS Unary Size tree of type OneUnary
 * where OneUnary = {Name:string;ResultWidth:int;Head:OneUnary option;Tail:OneUnary option;Elements:OneUnary list}
 * Seems unused currently?
 */
export function RHSUnaryAnalysis(assignmentRHS, inputWireSizeMap, paramMap) {
    const findSizeOfExpression = (tree) => {
        let matchResult, u, a, b, a_2, b_2;
        switch (tree.tag) {
            case 13: {
                matchResult = 0;
                u = tree.fields[0];
                break;
            }
            case 12: {
                matchResult = 0;
                u = tree.fields[0];
                break;
            }
            case 2: {
                matchResult = 1;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 3: {
                matchResult = 1;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 4: {
                matchResult = 1;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 5: {
                matchResult = 1;
                a = tree.fields[0];
                b = tree.fields[1];
                break;
            }
            case 9: {
                matchResult = 1;
                a = tree.fields[1];
                b = tree.fields[2];
                break;
            }
            case 15: {
                matchResult = 2;
                break;
            }
            case 8: {
                matchResult = 3;
                break;
            }
            case 11: {
                matchResult = 4;
                break;
            }
            case 0: {
                matchResult = 5;
                a_2 = tree.fields[0];
                b_2 = tree.fields[1];
                break;
            }
            case 1: {
                matchResult = 5;
                a_2 = tree.fields[0];
                b_2 = tree.fields[1];
                break;
            }
            default:
                matchResult = 6;
        }
        switch (matchResult) {
            case 0:
                switch (u.tag) {
                    case 1: {
                        const n = u.fields[0];
                        return new OneUnary("[number]", (n.tag === 0) ? n.fields[0] : 32, undefined, undefined, empty());
                    }
                    case 3: {
                        const unariesList = findSizeOfConcat(u.fields[0])(empty());
                        return new OneUnary("{...}", fold_1((s, unary) => ((s + unary.ResultWidth) | 0), 0, unariesList), undefined, undefined, unariesList);
                    }
                    case 2: {
                        const elements = findSizeOfExpression(u.fields[0]);
                        return new OneUnary("(...)", elements.ResultWidth, undefined, undefined, singleton(elements));
                    }
                    case 4: {
                        const paramName = u.fields[0];
                        const bits_1 = u.fields[1];
                        const value = find(getPrimaryName(paramName), paramMap) | 0;
                        new Number$(/* All */ 0, [parse(bits_1, 511, false, 32), BaseDU.Decimal, int32ToString(value), getPrimaryLocation(paramName)]);
                        return new OneUnary("(...)", parse(bits_1, 511, false, 32), undefined, undefined, empty());
                    }
                    default: {
                        const primary = u.fields[0];
                        let matchResult_1, id;
                        switch (primary.tag) {
                            case 5: {
                                matchResult_1 = 0;
                                id = primary.fields[0];
                                break;
                            }
                            case 6: {
                                matchResult_1 = 1;
                                break;
                            }
                            case 3: {
                                matchResult_1 = 2;
                                break;
                            }
                            case 1: {
                                matchResult_1 = 3;
                                break;
                            }
                            case 2: {
                                matchResult_1 = 4;
                                break;
                            }
                            case 4: {
                                matchResult_1 = 5;
                                break;
                            }
                            default: {
                                matchResult_1 = 0;
                                id = primary.fields[0];
                            }
                        }
                        switch (matchResult_1) {
                            case 0: {
                                const matchValue = tryFind(id.Name, inputWireSizeMap);
                                if (matchValue == null) {
                                    return new OneUnary("undefined", 0, undefined, undefined, empty());
                                }
                                else {
                                    return new OneUnary(id.Name, matchValue, undefined, undefined, empty());
                                }
                            }
                            case 1: {
                                const id_1 = primary.fields[0];
                                const matchValue_1 = tryFind(id_1.Name, inputWireSizeMap);
                                if (matchValue_1 == null) {
                                    return new OneUnary("undefined", 0, undefined, undefined, empty());
                                }
                                else {
                                    return new OneUnary(id_1.Name, matchValue_1, undefined, undefined, empty());
                                }
                            }
                            case 2:
                                return new OneUnary(primary.fields[0].Name, 1, undefined, undefined, empty());
                            case 3:
                                return new OneUnary(primary.fields[0].Name, 1, undefined, undefined, empty());
                            case 4:
                                return new OneUnary(primary.fields[0].Name, (primary.fields[1] - primary.fields[2]) + 1, undefined, undefined, empty());
                            default:
                                return new OneUnary(primary.fields[0].Name, primary.fields[2], undefined, undefined, empty());
                        }
                    }
                }
            case 1: {
                const u1 = findSizeOfExpression(a);
                return new OneUnary("[bitwise_op]", u1.ResultWidth, u1, findSizeOfExpression(b), empty());
            }
            case 2: {
                const result = findSizeOfExpression(tree.fields[0]);
                const u1_1 = findSizeOfExpression(tree.fields[1]);
                return new OneUnary("[conditional]", u1_1.ResultWidth, u1_1, findSizeOfExpression(tree.fields[2]), singleton(result));
            }
            case 3: {
                const b_1 = tree.fields[2];
                const u1_2 = findSizeOfExpression(tree.fields[1]);
                const u2_2 = findSizeOfExpression(b_1);
                if (b_1.tag === 14) {
                    return new OneUnary("[shift]", u1_2.ResultWidth, u1_2, undefined, empty());
                }
                else {
                    return new OneUnary("[shift]", u1_2.ResultWidth, u1_2, u2_2, empty());
                }
            }
            case 4: {
                const e_1 = tree.fields[1];
                let matchResult_2, p;
                if (e_1.tag === 13) {
                    if (e_1.fields[0].tag === 2) {
                        matchResult_2 = 0;
                        p = e_1.fields[0].fields[0];
                    }
                    else {
                        matchResult_2 = 1;
                    }
                }
                else {
                    matchResult_2 = 1;
                }
                switch (matchResult_2) {
                    case 0:
                        return new OneUnary("[reduction]", 1, undefined, undefined, singleton(findSizeOfExpression(p)));
                    default:
                        return new OneUnary("[reduction]", 1, undefined, undefined, empty());
                }
            }
            case 5:
                return new OneUnary("[logical_op]", 1, findSizeOfExpression(a_2), findSizeOfExpression(b_2), empty());
            default:
                return toFail(printf("Case not covered!"));
        }
    };
    const findSizeOfConcat = (elements_1) => ((concatList) => fold((acc, unary_1) => append(acc, singleton(unary_1)), concatList, map_1(findSizeOfExpression, elements_1)));
    return findSizeOfExpression(assignmentRHS);
}

export function getWidthOfExpr(assignmentRHS, inputWireSizeMap, arraySizeMap, paramMap) {
    const findSizeOfExpression = (tree_mut) => {
        findSizeOfExpression:
        while (true) {
            const tree = tree_mut;
            let matchResult, primary, n, e, e_1, bits_1, paramName, head, tail, f, t, a, head_1, tail_1;
            switch (tree.tag) {
                case 11: {
                    matchResult = 8;
                    break;
                }
                case 0:
                case 1: {
                    matchResult = 9;
                    break;
                }
                case 6: {
                    matchResult = 10;
                    break;
                }
                case 7: {
                    matchResult = 11;
                    break;
                }
                case 12: {
                    switch (tree.fields[0].tag) {
                        case 0: {
                            matchResult = 0;
                            primary = tree.fields[0].fields[0];
                            break;
                        }
                        case 2: {
                            matchResult = 3;
                            e_1 = tree.fields[0].fields[0];
                            break;
                        }
                        default:
                            matchResult = 13;
                    }
                    break;
                }
                case 14: {
                    matchResult = 1;
                    n = tree.fields[0];
                    break;
                }
                case 2: {
                    matchResult = 5;
                    head = tree.fields[0];
                    tail = tree.fields[1];
                    break;
                }
                case 3: {
                    matchResult = 5;
                    head = tree.fields[0];
                    tail = tree.fields[1];
                    break;
                }
                case 4: {
                    matchResult = 5;
                    head = tree.fields[0];
                    tail = tree.fields[1];
                    break;
                }
                case 5: {
                    matchResult = 5;
                    head = tree.fields[0];
                    tail = tree.fields[1];
                    break;
                }
                case 9: {
                    matchResult = 5;
                    head = tree.fields[1];
                    tail = tree.fields[2];
                    break;
                }
                case 15: {
                    matchResult = 6;
                    f = tree.fields[2];
                    t = tree.fields[1];
                    break;
                }
                case 8: {
                    matchResult = 7;
                    a = tree.fields[1];
                    break;
                }
                case 10: {
                    matchResult = 12;
                    head_1 = tree.fields[1];
                    tail_1 = tree.fields[2];
                    break;
                }
                default:
                    switch (tree.fields[0].tag) {
                        case 1: {
                            matchResult = 1;
                            n = tree.fields[0].fields[0];
                            break;
                        }
                        case 3: {
                            matchResult = 2;
                            e = tree.fields[0].fields[0];
                            break;
                        }
                        case 2: {
                            matchResult = 3;
                            e_1 = tree.fields[0].fields[0];
                            break;
                        }
                        case 4: {
                            matchResult = 4;
                            bits_1 = tree.fields[0].fields[1];
                            paramName = tree.fields[0].fields[0];
                            break;
                        }
                        default: {
                            matchResult = 0;
                            primary = tree.fields[0].fields[0];
                        }
                    }
            }
            switch (matchResult) {
                case 0:
                    switch (primary.tag) {
                        case 5:
                            return ((primary.fields[2] - primary.fields[3]) + 1) | 0;
                        case 6:
                        case 3:
                            return 1;
                        case 1: {
                            const matchValue_2 = tryFind(primary.fields[0].Name, arraySizeMap);
                            if (matchValue_2 == null) {
                                return 1;
                            }
                            else {
                                matchValue_2[1];
                                return matchValue_2[0] | 0;
                            }
                        }
                        case 2:
                            return ((primary.fields[1] - primary.fields[2]) + 1) | 0;
                        case 4:
                            return primary.fields[2] | 0;
                        default: {
                            const id = primary.fields[0];
                            const matchValue = tryFind(id.Name, inputWireSizeMap);
                            if (matchValue == null) {
                                const matchValue_1 = tryFind(id.Name, paramMap);
                                if (matchValue_1 == null) {
                                    return 0;
                                }
                                else {
                                    return 32;
                                }
                            }
                            else {
                                return matchValue | 0;
                            }
                        }
                    }
                case 1:
                    if (n.tag === 0) {
                        return n.fields[0] | 0;
                    }
                    else {
                        return 32;
                    }
                case 2:
                    return sum(findSizeOfConcat(e)(empty()), {
                        GetZero: () => 0,
                        Add: (x, y) => ((x + y) | 0),
                    }) | 0;
                case 3: {
                    tree_mut = e_1;
                    continue findSizeOfExpression;
                }
                case 4: {
                    const matchValue_3 = tryFind(getPrimaryName(paramName), paramMap);
                    if (matchValue_3 == null) {
                        return 32;
                    }
                    else {
                        return parse(bits_1, 511, false, 32) | 0;
                    }
                }
                case 5:
                    return max(findSizeOfExpression(head), findSizeOfExpression(tail)) | 0;
                case 6:
                    return max(findSizeOfExpression(t), findSizeOfExpression(f)) | 0;
                case 7: {
                    tree_mut = a;
                    continue findSizeOfExpression;
                }
                case 8:
                    return 1;
                case 9:
                    return 1;
                case 10:
                    return 1;
                case 11:
                    return 1;
                case 12:
                    return (findSizeOfExpression(head_1) + findSizeOfExpression(tail_1)) | 0;
                default:
                    return toFail(printf("Case not covered!")) | 0;
            }
            break;
        }
    };
    const findSizeOfConcat = (elements_1) => ((concatList) => fold((acc, w) => append(acc, singleton(w)), concatList, map_1(findSizeOfExpression, elements_1, Int32Array)));
    return findSizeOfExpression(assignmentRHS) | 0;
}

/**
 * Check if the width of each wire/input used
 * is within the correct range (defined range)
 */
export function checkPrimariesWidths(linesLocations, currentInputWireSizeMap, arraySizeMap, paramMap, localErrors, primariesRHS, numbersRHS) {
    return append(collect((x) => {
        let matchValue_3, arrayWidth_1, arrayDims_1, definition_1, arg_7, arg_8, usedWidth_1, arg_12, matchValue_9, bitIndex, size, definition_3, arg_25, usedWidth_3;
        switch (x.tag) {
            case 5: {
                const indices = x.fields[1];
                const id_1 = x.fields[0];
                const bStart = x.fields[2] | 0;
                const bEnd = x.fields[3] | 0;
                let arrayIndexErrors;
                const matchValue = item_2(0, indices);
                const matchValue_1 = tryFind(id_1.Name, arraySizeMap);
                let matchResult, arrayDims, arrayWidth, idxInt;
                if (matchValue.tag === 0) {
                    if (matchValue_1 != null) {
                        matchResult = 0;
                        arrayDims = matchValue_1[1];
                        arrayWidth = matchValue_1[0];
                        idxInt = matchValue.fields[0];
                    }
                    else {
                        matchResult = 1;
                    }
                }
                else {
                    matchResult = 1;
                }
                switch (matchResult) {
                    case 0: {
                        if ((idxInt < item_2(0, arrayDims)) && (idxInt >= 0)) {
                            arrayIndexErrors = empty();
                        }
                        else {
                            let definition;
                            const arg_1 = (item_2(0, arrayDims) - 1) | 0;
                            const arg_2 = (arrayWidth - 1) | 0;
                            definition = toText(printf(" %s[%i:0][%i:0] "))(id_1.Name)(arg_1)(arg_2);
                            const usedWidth = toText(printf(" %s[%i] "))(id_1.Name)(idxInt);
                            arrayIndexErrors = createErrorMessage(linesLocations, id_1.Location, toText(printf("Wrong width of array")), [new ExtraErrorInfo((((toText(printf("Array: \'%s\' is defined as"))(id_1.Name) + definition) + "\nTherefore,") + usedWidth) + "is invalid", false, ReplaceType.NoReplace)], id_1.Name);
                        }
                        break;
                    }
                    default:
                        arrayIndexErrors = empty();
                }
                if (isEmpty(arrayIndexErrors)) {
                    return append(localErrors, (matchValue_3 = tryFind(id_1.Name, arraySizeMap), (matchValue_3 != null) ? ((arrayWidth_1 = (matchValue_3[0] | 0), (arrayDims_1 = matchValue_3[1], (((bStart < arrayWidth_1) && (bEnd >= 0)) && (bStart >= bEnd)) ? empty() : ((definition_1 = ((arrayWidth_1 === 1) ? " a single bit " : ((arg_7 = ((item_2(0, arrayDims_1) - 1) | 0), (arg_8 = ((arrayWidth_1 - 1) | 0), toText(printf(" %s[%i:0][%i:0] "))(id_1.Name)(arg_7)(arg_8))))), (usedWidth_1 = ((bStart === bEnd) ? toText(printf(" %s[%i] "))(id_1.Name)(bStart) : ((arg_12 = item_2(0, indices), toText(printf(" %s[%A:0][%i:%i] "))(id_1.Name)(arg_12)(bStart)(bEnd)))), createErrorMessage(linesLocations, id_1.Location, toText(printf("Wrong width of array: \'%s\'"))(id_1.Name), [new ExtraErrorInfo((((toText(printf("Array: \'%s\' is defined as"))(id_1.Name) + definition_1) + "\nTherefore,") + usedWidth_1) + "is invalid", false, ReplaceType.NoReplace)], id_1.Name))))))) : empty()));
                }
                else {
                    return append(localErrors, arrayIndexErrors);
                }
            }
            case 6: {
                const id_2 = x.fields[0];
                let arrayIndexErrors_1;
                const matchValue_5 = item_2(0, x.fields[1]);
                const matchValue_6 = tryFind(id_2.Name, arraySizeMap);
                let matchResult_1, arrayDims_2, arrayWidth_2, idxInt_1;
                if (matchValue_5.tag === 0) {
                    if (matchValue_6 != null) {
                        matchResult_1 = 0;
                        arrayDims_2 = matchValue_6[1];
                        arrayWidth_2 = matchValue_6[0];
                        idxInt_1 = matchValue_5.fields[0];
                    }
                    else {
                        matchResult_1 = 1;
                    }
                }
                else {
                    matchResult_1 = 1;
                }
                switch (matchResult_1) {
                    case 0: {
                        if ((idxInt_1 < item_2(0, arrayDims_2)) && (idxInt_1 >= 0)) {
                            arrayIndexErrors_1 = empty();
                        }
                        else {
                            let definition_2;
                            const arg_18 = (item_2(0, arrayDims_2) - 1) | 0;
                            const arg_19 = (arrayWidth_2 - 1) | 0;
                            definition_2 = toText(printf(" %s[%i:0][%i:0] "))(id_2.Name)(arg_18)(arg_19);
                            const usedWidth_2 = toText(printf(" %s[%i] "))(id_2.Name)(idxInt_1);
                            arrayIndexErrors_1 = createErrorMessage(linesLocations, id_2.Location, toText(printf("Wrong width of array: \'%s\'"))(id_2.Name), [new ExtraErrorInfo((((toText(printf("Array: \'%s\' is defined as"))(id_2.Name) + definition_2) + "\nTherefore,") + usedWidth_2) + "is invalid", false, ReplaceType.NoReplace)], id_2.Name);
                        }
                        break;
                    }
                    default:
                        arrayIndexErrors_1 = empty();
                }
                if (isEmpty(arrayIndexErrors_1)) {
                    let checkedIndex;
                    try {
                        checkedIndex = evalExprWithParams(x.fields[2], paramMap);
                    }
                    catch (matchValue_8) {
                        checkedIndex = undefined;
                    }
                    return append(localErrors, (matchValue_9 = tryFind(id_2.Name, currentInputWireSizeMap), (checkedIndex != null) ? ((matchValue_9 != null) ? ((bitIndex = (checkedIndex | 0), (size = (matchValue_9 | 0), ((bitIndex < size) && (bitIndex >= 0)) ? localErrors : ((definition_3 = ((size === 1) ? " a single bit " : ((arg_25 = ((size - 1) | 0), toText(printf(" %s[%i:0] "))(id_2.Name)(arg_25)))), (usedWidth_3 = toText(printf(" %s[%i] "))(id_2.Name)(bitIndex), createErrorMessage(linesLocations, id_2.Location, toText(printf("Wrong width of variable: \'%s\'"))(id_2.Name), [new ExtraErrorInfo((((toText(printf("Variable: \'%s\' is defined as"))(id_2.Name) + definition_3) + "\nTherefore,") + usedWidth_3) + "is invalid", false, ReplaceType.NoReplace)], id_2.Name))))))) : empty()) : empty()));
                }
                else {
                    return append(localErrors, arrayIndexErrors_1);
                }
            }
            case 3: {
                const id_3 = x.fields[0];
                let checkedIndex_1;
                try {
                    checkedIndex_1 = evalExprWithParams(x.fields[1], paramMap);
                }
                catch (matchValue_11) {
                    checkedIndex_1 = undefined;
                }
                const matchValue_12 = tryFind(id_3.Name, currentInputWireSizeMap);
                let matchResult_2, bitIndex_1, size_1;
                if (checkedIndex_1 != null) {
                    if (matchValue_12 != null) {
                        matchResult_2 = 0;
                        bitIndex_1 = checkedIndex_1;
                        size_1 = matchValue_12;
                    }
                    else {
                        matchResult_2 = 1;
                    }
                }
                else {
                    matchResult_2 = 1;
                }
                switch (matchResult_2) {
                    case 0:
                        if ((bitIndex_1 < size_1) && (bitIndex_1 >= 0)) {
                            return localErrors;
                        }
                        else {
                            let definition_4;
                            if (size_1 === 1) {
                                definition_4 = " a single bit ";
                            }
                            else {
                                const arg_31 = (size_1 - 1) | 0;
                                definition_4 = toText(printf(" %s[%i:0] "))(id_3.Name)(arg_31);
                            }
                            const usedWidth_4 = toText(printf(" %s[%i] "))(id_3.Name)(bitIndex_1);
                            return append(localErrors, createErrorMessage(linesLocations, id_3.Location, toText(printf("Wrong width of variable: \'%s\'"))(id_3.Name), [new ExtraErrorInfo((((toText(printf("Variable: \'%s\' is defined as"))(id_3.Name) + definition_4) + "\nTherefore,") + usedWidth_4) + "is invalid", false, ReplaceType.NoReplace)], id_3.Name));
                        }
                    default:
                        return localErrors;
                }
            }
            case 1: {
                const id_4 = x.fields[0];
                const bStart_1 = x.fields[1] | 0;
                const bEnd_1 = bStart_1 | 0;
                const matchValue_14 = tryFind(id_4.Name, arraySizeMap);
                if (matchValue_14 == null) {
                    const matchValue_15 = tryFind(id_4.Name, currentInputWireSizeMap);
                    if (matchValue_15 == null) {
                        return localErrors;
                    }
                    else {
                        const size_2 = matchValue_15 | 0;
                        if ((bStart_1 < size_2) && (bEnd_1 >= 0)) {
                            return localErrors;
                        }
                        else {
                            let definition_6;
                            if (size_2 === 1) {
                                definition_6 = " a single bit ";
                            }
                            else {
                                const arg_44 = (size_2 - 1) | 0;
                                definition_6 = toText(printf(" %s[%i:0] "))(id_4.Name)(arg_44);
                            }
                            const usedWidth_6 = toText(printf(" %s[%i] "))(id_4.Name)(bStart_1);
                            return append(localErrors, createErrorMessage(linesLocations, id_4.Location, toText(printf("Wrong width of variable: \'%s\'"))(id_4.Name), [new ExtraErrorInfo((((toText(printf("Variable: \'%s\' is defined as"))(id_4.Name) + definition_6) + "\nTherefore,") + usedWidth_6) + "is invalid", false, ReplaceType.NoReplace)], id_4.Name));
                        }
                    }
                }
                else {
                    const arrayWidth_3 = matchValue_14[0] | 0;
                    const arrayDims_3 = matchValue_14[1];
                    if ((bStart_1 < item_2(0, arrayDims_3)) && (bEnd_1 >= 0)) {
                        return localErrors;
                    }
                    else {
                        let definition_5;
                        const arg_37 = (item_2(0, arrayDims_3) - 1) | 0;
                        const arg_38 = (arrayWidth_3 - 1) | 0;
                        definition_5 = toText(printf(" %s[%i:0][%i:0] "))(id_4.Name)(arg_37)(arg_38);
                        const usedWidth_5 = toText(printf(" %s[%i] "))(id_4.Name)(bStart_1);
                        return append(localErrors, createErrorMessage(linesLocations, id_4.Location, toText(printf("Wrong width of array: \'%s\'"))(id_4.Name), [new ExtraErrorInfo((((toText(printf("Array: \'%s\' is defined as"))(id_4.Name) + definition_5) + "\nTherefore,") + usedWidth_5) + "is invalid", false, ReplaceType.NoReplace)], id_4.Name));
                    }
                }
            }
            case 2: {
                const id_5 = x.fields[0];
                const bStart_2 = x.fields[1] | 0;
                const bEnd_2 = x.fields[2] | 0;
                const matchValue_16 = tryFind(id_5.Name, currentInputWireSizeMap);
                if (matchValue_16 == null) {
                    return localErrors;
                }
                else {
                    const size_3 = matchValue_16 | 0;
                    if (((bStart_2 < size_3) && (bEnd_2 >= 0)) && (bStart_2 >= bEnd_2)) {
                        return localErrors;
                    }
                    else {
                        let definition_7;
                        if (size_3 === 1) {
                            definition_7 = " a single bit ";
                        }
                        else {
                            const arg_50 = (size_3 - 1) | 0;
                            definition_7 = toText(printf(" %s[%i:0] "))(id_5.Name)(arg_50);
                        }
                        const usedWidth_7 = (bStart_2 === bEnd_2) ? toText(printf(" %s[%i] "))(id_5.Name)(bStart_2) : toText(printf(" %s[%i:%i] "))(id_5.Name)(bStart_2)(bEnd_2);
                        return append(localErrors, createErrorMessage(linesLocations, id_5.Location, toText(printf("Wrong width of variable: \'%s\'"))(id_5.Name), [new ExtraErrorInfo((((toText(printf("Variable: \'%s\' is defined as"))(id_5.Name) + definition_7) + "\nTherefore,") + usedWidth_7) + "is invalid", false, ReplaceType.NoReplace)], id_5.Name));
                    }
                }
            }
            case 4: {
                const id_6 = x.fields[0];
                const bStart_3 = evalExpr(x.fields[1]) | 0;
                const bEnd_3 = ((bStart_3 - x.fields[2]) + 1) | 0;
                const matchValue_18 = tryFind(id_6.Name, currentInputWireSizeMap);
                if (matchValue_18 == null) {
                    return localErrors;
                }
                else {
                    const size_4 = matchValue_18 | 0;
                    if ((bStart_3 < size_4) && (bEnd_3 >= 0)) {
                        return localErrors;
                    }
                    else {
                        let definition_8;
                        if (size_4 === 1) {
                            definition_8 = " a single bit ";
                        }
                        else {
                            const arg_59 = (size_4 - 1) | 0;
                            definition_8 = toText(printf(" %s[%i:0] "))(id_6.Name)(arg_59);
                        }
                        const usedWidth_8 = toText(printf(" %s[%i:%i] "))(id_6.Name)(bStart_3)(bEnd_3);
                        return append(localErrors, createErrorMessage(linesLocations, id_6.Location, toText(printf("Wrong width of variable: \'%s\'"))(id_6.Name), [new ExtraErrorInfo((((toText(printf("Variable: \'%s\' is defined as"))(id_6.Name) + definition_8) + "\nTherefore,") + usedWidth_8) + "is invalid", false, ReplaceType.NoReplace)], id_6.Name));
                    }
                }
            }
            default:
                return localErrors;
        }
    }, primariesRHS), collect((n) => {
        if (n.tag === 0) {
            const numBase = n.fields[1];
            const loc = n.fields[3] | 0;
            const bits = n.fields[0] | 0;
            if (bits === 0) {
                return append(localErrors, createErrorMessage(linesLocations, loc, "Number can\'t be 0 bits wide", [new ExtraErrorInfo("Number can\'t be 0 bits wide", false, ReplaceType.NoReplace), new ExtraErrorInfo("The integer before \'h/\'b represents the width of the number\n e.g. 12\'hc7 -> 000011000111", false, ReplaceType.NoReplace)], "0\'b"));
            }
            else {
                const matchValue_19 = strToIntCheckWidth(bits, toString_1(toDecimal(n.fields[2], (numBase.tag === 2) ? "\'h" : ((numBase.tag === 1) ? "\'d" : "\'b"), "64")));
                if (matchValue_19.tag === 1) {
                    return append(localErrors, createErrorMessage(linesLocations, loc, toText(printf("Number can\'t fit in %i bits"))(bits), [new ExtraErrorInfo(toText(printf("Number can\'t fit in %i bits"))(bits), false, ReplaceType.NoReplace), new ExtraErrorInfo("The integer before \'h/\'b represents the width of the number\n e.g. 12\'hc7 -> 000011000111", false, ReplaceType.NoReplace)], "0\'b"));
                }
                else {
                    return localErrors;
                }
            }
        }
        else {
            return localErrors;
        }
    }, numbersRHS));
}

export function checkExpr(linesLocations, currentInputWireSizeMap, arraySizeMap, paramMap, localErrors, expr) {
    return checkPrimariesWidths(linesLocations, currentInputWireSizeMap, arraySizeMap, paramMap, localErrors, primariesUsedInAssignment(empty(), expr), numbersUsedInAssignment(empty(), expr));
}

export function checkNumber(linesLocations, num) {
    const matchValue = value_2(num.Base);
    const matchValue_1 = value_2(num.AllNumber);
    const width = value_2(num.Bits);
    const numBase = matchValue;
    const allNum = matchValue_1;
    if (parse(width, 511, false, 32) === 0) {
        return createErrorMessage(linesLocations, num.Location, "Number can\'t be 0 bits wide", [new ExtraErrorInfo("Number can\'t be 0 bits wide", false, ReplaceType.NoReplace), new ExtraErrorInfo("The integer before \'h/\'b represents the width of the number\n e.g. 12\'hc7 -> 000011000111", false, ReplaceType.NoReplace)], "0\'b");
    }
    else {
        switch (numBase) {
            case "\'b": {
                "0" + allNum;
                break;
            }
            case "\'h": {
                "0x" + allNum;
                break;
            }
            default:
                allNum;
        }
        const no_1 = toDecimal(allNum, numBase, "64");
        const matchValue_3 = strToIntCheckWidth(parse(width, 511, false, 32), toString_1(no_1));
        if (matchValue_3.tag === 1) {
            return createErrorMessage(linesLocations, num.Location, toText(printf("Number can\'t fit in %A bits"))(width), [new ExtraErrorInfo(toText(printf("Number can\'t fit in %A bits"))(width), false, ReplaceType.NoReplace), new ExtraErrorInfo("The integer before \'h/\'b represents the width of the number\n e.g. 12\'hc7 -> 000011000111", false, ReplaceType.NoReplace)], "0\'b");
        }
        else {
            return empty();
        }
    }
}

export class ExpressionNode extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Expression", "Unary", "Number", "Primary"];
    }
}

export function ExpressionNode_$reflection() {
    return union_type("ErrorCheckHelpers.ExpressionNode", [], ExpressionNode, () => [[["Item", ExpressionT_$reflection()]], [["Item", UnaryT_$reflection()]], [["Item", NumberT_$reflection()]], [["Item", PrimaryT_$reflection()]]]);
}

/**
 * make sure to include variables AND ports in portSizeMap
 */
export function getRHSBits(portSizeMap, expression) {
    const getExprBits = (expr) => {
        const leftBits = (expr.tag === 0) ? getExprBits(expr.fields[0]) : ((expr.tag === 1) ? getExprBits(expr.fields[0]) : ((expr.tag === 2) ? getExprBits(expr.fields[0]) : ((expr.tag === 3) ? getExprBits(expr.fields[0]) : ((expr.tag === 4) ? getExprBits(expr.fields[0]) : ((expr.tag === 5) ? getExprBits(expr.fields[0]) : ((expr.tag === 6) ? getExprBits(expr.fields[1]) : ((expr.tag === 7) ? getExprBits(expr.fields[1]) : ((expr.tag === 8) ? getExprBits(expr.fields[1]) : ((expr.tag === 9) ? getExprBits(expr.fields[1]) : ((expr.tag === 10) ? getExprBits(expr.fields[1]) : ((expr.tag === 15) ? getExprBits(expr.fields[0]) : empty_1({
            Compare: (x, y) => (comparePrimitives(x, y) | 0),
        }))))))))))));
        const rightBits = (expr.tag === 0) ? getExprBits(expr.fields[1]) : ((expr.tag === 1) ? getExprBits(expr.fields[1]) : ((expr.tag === 2) ? getExprBits(expr.fields[1]) : ((expr.tag === 3) ? getExprBits(expr.fields[1]) : ((expr.tag === 4) ? getExprBits(expr.fields[1]) : ((expr.tag === 5) ? getExprBits(expr.fields[1]) : ((expr.tag === 6) ? getExprBits(expr.fields[2]) : ((expr.tag === 7) ? getExprBits(expr.fields[2]) : ((expr.tag === 8) ? getExprBits(expr.fields[2]) : ((expr.tag === 9) ? getExprBits(expr.fields[2]) : ((expr.tag === 10) ? getExprBits(expr.fields[2]) : ((expr.tag === 15) ? union(getExprBits(expr.fields[1]), getExprBits(expr.fields[2])) : empty_1({
            Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
        }))))))))))));
        let unaryBits;
        let matchResult, u;
        switch (expr.tag) {
            case 13: {
                matchResult = 0;
                u = expr.fields[0];
                break;
            }
            case 12: {
                matchResult = 0;
                u = expr.fields[0];
                break;
            }
            case 11: {
                matchResult = 1;
                break;
            }
            case 14: {
                matchResult = 2;
                break;
            }
            default:
                matchResult = 3;
        }
        switch (matchResult) {
            case 0: {
                switch (u.tag) {
                    case 1: {
                        unaryBits = empty_1({
                            Compare: (x_2, y_2) => (comparePrimitives(x_2, y_2) | 0),
                        });
                        break;
                    }
                    case 3: {
                        const array_1 = map_1(getExprBits, u.fields[0]);
                        unaryBits = fold(union, empty_1({
                            Compare: (x_3, y_3) => (comparePrimitives(x_3, y_3) | 0),
                        }), array_1);
                        break;
                    }
                    case 0: {
                        const primary = u.fields[0];
                        unaryBits = union(ofList_1(getPrimaryBits(portSizeMap, primary), {
                            Compare: (x_4, y_4) => (comparePrimitives(x_4, y_4) | 0),
                        }), (primary.tag === 3) ? getExprBits(primary.fields[1]) : ((primary.tag === 2) ? empty_1({
                            Compare: (x_6, y_6) => (comparePrimitives(x_6, y_6) | 0),
                        }) : ((primary.tag === 4) ? getExprBits(primary.fields[1]) : ((primary.tag === 0) ? empty_1({
                            Compare: (x_7, y_7) => (comparePrimitives(x_7, y_7) | 0),
                        }) : ((primary.tag === 5) ? empty_1({
                            Compare: (x_7, y_7) => (comparePrimitives(x_7, y_7) | 0),
                        }) : ((primary.tag === 6) ? empty_1({
                            Compare: (x_7, y_7) => (comparePrimitives(x_7, y_7) | 0),
                        }) : empty_1({
                            Compare: (x_5, y_5) => (comparePrimitives(x_5, y_5) | 0),
                        })))))));
                        break;
                    }
                    case 4: {
                        unaryBits = empty_1({
                            Compare: (x_8, y_8) => (comparePrimitives(x_8, y_8) | 0),
                        });
                        break;
                    }
                    default:
                        unaryBits = getExprBits(u.fields[0]);
                }
                break;
            }
            case 1: {
                unaryBits = getExprBits(expr.fields[1]);
                break;
            }
            case 2: {
                unaryBits = empty_1({
                    Compare: (x_9, y_9) => (comparePrimitives(x_9, y_9) | 0),
                });
                break;
            }
            default:
                unaryBits = empty_1({
                    Compare: (x_10, y_10) => (comparePrimitives(x_10, y_10) | 0),
                });
        }
        return fold_1(union, empty_1({
            Compare: (x_11, y_11) => (comparePrimitives(x_11, y_11) | 0),
        }), ofArray([leftBits, rightBits, unaryBits]));
    };
    return getExprBits(expression);
}

export function getLHSWidth(assign, varSizeMap, arraySizeMap) {
    const matchValue = assign.LHS.PrimaryType;
    let matchResult, id_1;
    switch (matchValue.tag) {
        case 1: {
            matchResult = 1;
            id_1 = matchValue.fields[0];
            break;
        }
        case 3: {
            matchResult = 1;
            id_1 = matchValue.fields[0];
            break;
        }
        case 5: {
            matchResult = 2;
            break;
        }
        case 6: {
            matchResult = 3;
            break;
        }
        case 2: {
            matchResult = 4;
            break;
        }
        case 4: {
            matchResult = 5;
            break;
        }
        default:
            matchResult = 0;
    }
    switch (matchResult) {
        case 0:
            return defaultArg(tryFind(matchValue.fields[0].Name, varSizeMap), 0) | 0;
        case 1: {
            const matchValue_1 = tryFind(id_1.Name, arraySizeMap);
            if (matchValue_1 == null) {
                return 1;
            }
            else {
                matchValue_1[1];
                return matchValue_1[0] | 0;
            }
        }
        case 2:
            return ((matchValue.fields[2] - matchValue.fields[3]) + 1) | 0;
        case 3:
            return find(matchValue.fields[0].Name, arraySizeMap)[0] | 0;
        case 4:
            return ((matchValue.fields[1] - matchValue.fields[2]) + 1) | 0;
        default:
            return matchValue.fields[2] | 0;
    }
}

export function getCondAndCaseExpressions(expressions, node) {
    switch (node.tag) {
        case 6: {
            const case$ = node.fields[0];
            return append(expressions, singleton([case$.Expression, case$.Location]));
        }
        case 8: {
            const cond = node.fields[0];
            return append(expressions, singleton([cond.Condition, cond.Location]));
        }
        default:
            return expressions;
    }
}

export function getModuleInstantiationStatements(moduleInstantiations, node) {
    if (node.tag === 22) {
        return append(moduleInstantiations, singleton(node.fields[0]));
    }
    else {
        return moduleInstantiations;
    }
}

/**
 * Helper function to find the closest port or wire name
 * Used by checkNamesOnRHSOfAssignment
 * Gives an appropriate suggestion if the wrong name is close to a name in the list
 */
export function findCloseVariable(variable, portAndWireNames) {
    return sortBy((str) => (str.length | 0), collect((name) => {
        const one = except(name.split(""), variable, {
            Equals: (x, y) => (x === y),
            GetHashCode: (x) => (stringHash(x) | 0),
        });
        const two = except(variable, name.split(""), {
            Equals: (x_1, y_1) => (x_1 === y_1),
            GetHashCode: (x_1) => (stringHash(x_1) | 0),
        });
        if ((length_1(one) === 0) && (length_1(two) <= 2)) {
            return singleton(name);
        }
        else if ((length_1(two) === 0) && (length_1(one) <= 2)) {
            return singleton(name);
        }
        else {
            return empty();
        }
    }, portAndWireNames), {
        Compare: (x_2, y_2) => (comparePrimitives(x_2, y_2) | 0),
    });
}

/**
 * Output primaries
 */
export function getModuleInstantiationOutputPrimaries(modInst, project) {
    const matchValue = tryFind_1((c) => (c.Name === modInst.Module.Name), project.LoadedComponents);
    if (matchValue == null) {
        return map((conn_2) => conn_2.Primary, ofArray(modInst.Connections));
    }
    else {
        const outputs = ofList_1(map((tuple) => tuple[0], matchValue.OutputLabels), {
            Compare: (x, y) => (comparePrimitives(x, y) | 0),
        });
        return map((conn_1) => conn_1.Primary, filter((conn) => contains(conn.PortId.Name.toLocaleUpperCase(), outputs), ofArray(modInst.Connections)));
    }
}

/**
 * Input primaries
 */
export function getModuleInstantiationInputPrimaries(modInst, project) {
    const matchValue = tryFind_1((c) => (c.Name === modInst.Module.Name), project.LoadedComponents);
    if (matchValue == null) {
        return map((conn_2) => conn_2.Primary, ofArray(modInst.Connections));
    }
    else {
        const inputs = ofList_1(map((tuple) => tuple[0], matchValue.InputLabels), {
            Compare: (x, y) => (comparePrimitives(x, y) | 0),
        });
        return map((conn_1) => conn_1.Primary, filter((conn) => contains(conn.PortId.Name.toLocaleUpperCase(), inputs), ofArray(modInst.Connections)));
    }
}

/**
 * Helper functions to estimate size of memory to set limits
 * Counts assignment, expression nodes, and primaries, as well as indices and ranges
 */
export function estimateExprCost(expr) {
    let matchResult, a, b;
    switch (expr.tag) {
        case 1: {
            matchResult = 0;
            a = expr.fields[0];
            b = expr.fields[1];
            break;
        }
        case 2: {
            matchResult = 0;
            a = expr.fields[0];
            b = expr.fields[1];
            break;
        }
        case 3: {
            matchResult = 0;
            a = expr.fields[0];
            b = expr.fields[1];
            break;
        }
        case 4: {
            matchResult = 0;
            a = expr.fields[0];
            b = expr.fields[1];
            break;
        }
        case 5: {
            matchResult = 0;
            a = expr.fields[0];
            b = expr.fields[1];
            break;
        }
        case 6: {
            matchResult = 0;
            a = expr.fields[1];
            b = expr.fields[2];
            break;
        }
        case 7: {
            matchResult = 0;
            a = expr.fields[1];
            b = expr.fields[2];
            break;
        }
        case 8: {
            matchResult = 0;
            a = expr.fields[1];
            b = expr.fields[2];
            break;
        }
        case 9: {
            matchResult = 0;
            a = expr.fields[1];
            b = expr.fields[2];
            break;
        }
        case 10: {
            matchResult = 0;
            a = expr.fields[1];
            b = expr.fields[2];
            break;
        }
        case 11: {
            matchResult = 1;
            break;
        }
        case 12: {
            matchResult = 2;
            break;
        }
        case 13: {
            matchResult = 3;
            break;
        }
        case 14: {
            matchResult = 4;
            break;
        }
        case 15: {
            matchResult = 5;
            break;
        }
        default: {
            matchResult = 0;
            a = expr.fields[0];
            b = expr.fields[1];
        }
    }
    switch (matchResult) {
        case 0:
            return ((1 + estimateExprCost(a)) + estimateExprCost(b)) | 0;
        case 1:
            return (1 + estimateExprCost(expr.fields[1])) | 0;
        case 2:
            return (1 + estimateUnaryCost(expr.fields[0])) | 0;
        case 3:
            return (1 + estimateUnaryCost(expr.fields[0])) | 0;
        case 4:
            return 1;
        default:
            return (((1 + estimateExprCost(expr.fields[0])) + estimateExprCost(expr.fields[1])) + estimateExprCost(expr.fields[2])) | 0;
    }
}

export function estimateUnaryCost(u) {
    switch (u.tag) {
        case 1:
            return 1;
        case 2:
            return (1 + estimateExprCost(u.fields[0])) | 0;
        case 3:
            return (1 + sum(map((expr) => (estimateExprCost(expr) | 0), ofArray(u.fields[0])), {
                GetZero: () => 0,
                Add: (x, y) => ((x + y) | 0),
            })) | 0;
        case 4:
            return 1;
        default:
            return (1 + estimatePrimaryCost(u.fields[0])) | 0;
    }
}

export function estimatePrimaryCost(p) {
    switch (p.tag) {
        case 1:
            return 2;
        case 3:
            return (1 + estimateExprCost(p.fields[1])) | 0;
        case 2:
            return 2;
        case 4:
            return (2 + estimateExprCost(p.fields[1])) | 0;
        case 5:
            return (2 + sum(map((a) => (estimateArraySelCost(a) | 0), ofArray(p.fields[1])), {
                GetZero: () => 0,
                Add: (x, y) => ((x + y) | 0),
            })) | 0;
        case 6:
            return ((1 + sum(map((a_1) => (estimateArraySelCost(a_1) | 0), ofArray(p.fields[1])), {
                GetZero: () => 0,
                Add: (x_1, y_1) => ((x_1 + y_1) | 0),
            })) + estimateExprCost(p.fields[2])) | 0;
        default:
            return 1;
    }
}

export function estimateArraySelCost(a) {
    if (a.tag === 1) {
        return estimateExprCost(a.fields[0]) | 0;
    }
    else {
        return 1;
    }
}

export function estimateAssignmentCost(a) {
    return ((1 + estimatePrimaryCost(a.LHS.PrimaryType)) + estimateExprCost(a.RHS)) | 0;
}

export function estimateStatementCost(stmt_mut) {
    estimateStatementCost:
    while (true) {
        const stmt = stmt_mut;
        let option_1, option_4;
        switch (stmt.tag) {
            case 0:
                return estimateAssignmentCost(stmt.fields[0]) | 0;
            case 2:
                return sum(map((stmt_1) => (estimateStatementCost(stmt_1) | 0), ofArray(stmt.fields[0])), {
                    GetZero: () => 0,
                    Add: (x, y) => ((x + y) | 0),
                }) | 0;
            case 3: {
                const c = stmt.fields[0];
                const casesCost = sum(map((item) => (estimateStatementCost(item.Statement) | 0), ofArray(c.CaseItems)), {
                    GetZero: () => 0,
                    Add: (x_1, y_1) => ((x_1 + y_1) | 0),
                }) | 0;
                const defaultCost = defaultArg((option_1 = c.Default, (option_1 != null) ? estimateStatementCost(option_1) : undefined), 0) | 0;
                return (((1 + estimateExprCost(c.Expression)) + casesCost) + defaultCost) | 0;
            }
            case 4: {
                const ifStmt = stmt.fields[0];
                const ifCost = estimateStatementCost(ifStmt.Statement) | 0;
                const elseCost = defaultArg((option_4 = stmt.fields[1], (option_4 != null) ? estimateStatementCost(option_4) : undefined), 0) | 0;
                return (((1 + estimateExprCost(ifStmt.Condition)) + ifCost) + elseCost) | 0;
            }
            case 5: {
                stmt_mut = stmt.fields[0].Statement;
                continue estimateStatementCost;
            }
            default:
                return estimateAssignmentCost(stmt.fields[0]) | 0;
        }
        break;
    }
}

